// A curated Marian library — the documents the Church herself has produced, organized by
// period. Summaries are editorial paraphrases, not verbatim document quotations.

export const LIBRARY_SECTIONS = [
  {
    id: "ancient",
    label: "Ancient prayer & the great Councils",
    kicker: "3rd–8th centuries",
    intro: "Early Marian prayer and two great Councils: witnesses to the Church’s faith in Christ and his Mother.",
    entries: [
      {
        id: "sub-tuum",
        title: "Sub Tuum Praesidium",
        author: "Egypt, Rylands Papyrus 470",
        year: "Early Christian prayer · manuscript dating debated",
        kind: "Prayer",
        why: "The oldest known prayer addressed to Mary — evidence that the Church invoked her intercession centuries before any dogma was defined.",
        excerpt: "This ancient prayer asks the Mother of God to receive the petitions of those in distress and intercede for their deliverance from danger.",
        citation: "Sub Tuum Praesidium · summary of the prayer; linked overview is a secondary source",
        url: "https://en.wikipedia.org/wiki/Sub_tuum_praesidium",
        urlLabel: "About the papyrus",
      },
      {
        id: "ephesus",
        title: "Council of Ephesus — Formula of Union",
        author: "Cyril of Alexandria & John of Antioch",
        year: "431 (Formula 433)",
        kind: "Ecumenical Council",
        why: "Defined Mary as Theotokos — the Mother of God. Every subsequent Marian doctrine rests on this single word.",
        excerpt: "The Formula of Union confesses one Christ, truly God and truly man. Because the Word personally assumed humanity from Mary, she is confessed as Theotokos, Mother of God.",
        citation: "Formula of Union (433) · letter of Cyril to John of Antioch, following Ephesus (431)",
        url: "https://www.papalencyclicals.net/councils/ecum03.htm",
        urlLabel: "Text at papalencyclicals.net",
      },
      {
        id: "nicaea-ii",
        title: "Second Council of Nicaea — Definition",
        author: "Fathers of Nicaea II",
        year: "787",
        kind: "Ecumenical Council",
        why: "Answered the iconoclasts: because God became man from Mary, he can be depicted — and so can she. Grounds the entire Eastern and Western tradition of Marian icons.",
        excerpt: "The Council receives the veneration of sacred images while reserving adoration for God alone. Honour shown to an image is directed to the person it represents.",
        citation: "Nicaea II (787) · Definition concerning holy images",
        url: "https://www.papalencyclicals.net/councils/ecum07.htm",
        urlLabel: "Text at papalencyclicals.net",
      },
    ],
  },
  {
    id: "definitions",
    label: "The Marian dogmas",
    kicker: "1854 · 1950",
    intro: "Two solemn definitions by the Roman Pontiffs, each answering a question the Church had lived with for centuries.",
    entries: [
      {
        id: "ineffabilis-deus",
        title: "Ineffabilis Deus",
        author: "Bl. Pius IX",
        year: "8 December 1854",
        kind: "Apostolic Constitution",
        why: "Solemn definition of the Immaculate Conception: from the first instant of her existence, Mary was preserved from every stain of original sin.",
        excerpt: "Pius IX defines Mary’s preservation from original sin from the first instant of her conception as a singular grace of God, granted in view of the merits of Jesus Christ, the Saviour of the human race.",
        citation: "Ineffabilis Deus · concluding dogmatic definition",
        url: "https://www.papalencyclicals.net/pius09/p9ineff.htm",
        urlLabel: "Full text",
      },
      {
        id: "munificentissimus",
        title: "Munificentissimus Deus",
        author: "Ven. Pius XII",
        year: "1 November 1950",
        kind: "Apostolic Constitution",
        why: "Solemn definition of the Assumption: at the end of her earthly life, Mary was taken up body and soul into heavenly glory.",
        excerpt: "Pius XII defines that the Immaculate Mother of God, the ever-Virgin Mary, was assumed body and soul into heavenly glory at the completion of her earthly life.",
        citation: "Munificentissimus Deus, §44",
        url: "https://www.vatican.va/content/pius-xii/en/apost_constitutions/documents/hf_p-xii_apc_19501101_munificentissimus-deus.html",
        urlLabel: "Vatican.va",
      },
    ],
  },
  {
    id: "vatican-ii",
    label: "Second Vatican Council",
    kicker: "1964",
    intro: "For the first time in the history of the ecumenical Councils, the Church's teaching on Mary was set within the Constitution on the Church herself — not treated as a separate parallel.",
    entries: [
      {
        id: "lumen-gentium",
        title: "Lumen Gentium, Chapter VIII",
        author: "Second Vatican Council · St. Paul VI",
        year: "21 November 1964",
        kind: "Dogmatic Constitution",
        why: "The Council's chapter on 'the Blessed Virgin Mary in the mystery of Christ and the Church' — the master text of contemporary Catholic Marian teaching.",
        excerpt: "The Council places Mary’s vocation within God’s plan for the Incarnation. As Mother of the Redeemer, his generous associate and the Lord’s humble servant, she remains a creature entirely dependent on Christ.",
        citation: "Lumen Gentium, §§53, 60–62",
        url: "https://www.vatican.va/archive/hist_councils/ii_vatican_council/documents/vat-ii_const_19641121_lumen-gentium_en.html",
        urlLabel: "Vatican.va (whole document)",
      },
    ],
  },
  {
    id: "modern-popes",
    label: "The modern Marian magisterium",
    kicker: "1974 · 1987 · 2002",
    intro: "An apostolic exhortation, an encyclical and an apostolic letter that shape the practice of Marian devotion today.",
    entries: [
      {
        id: "marialis-cultus",
        title: "Marialis Cultus",
        author: "St. Paul VI",
        year: "2 February 1974",
        kind: "Apostolic Exhortation",
        why: "The pastoral charter for authentic Marian devotion — biblical, liturgical, ecumenical, anthropological, oriented to Christ.",
        excerpt: "Paul VI calls for Marian devotion rooted in the Trinity and centred on Christ. It should be biblical, liturgical and attentive to the Holy Spirit, expressing genuine faith rather than empty sentiment.",
        citation: "Marialis Cultus, §§25–30, 38–39",
        url: "https://www.vatican.va/content/paul-vi/en/apost_exhortations/documents/hf_p-vi_exh_19740202_marialis-cultus.html",
        urlLabel: "Vatican.va",
      },
      {
        id: "redemptoris-mater",
        title: "Redemptoris Mater",
        author: "St. John Paul II",
        year: "25 March 1987",
        kind: "Encyclical",
        why: "JP II's great encyclical on the Blessed Virgin Mary in the life of the pilgrim Church — the theological complement of Lumen Gentium VIII.",
        excerpt: "Mary’s place in the Church cannot be separated from her relationship with Christ. Her maternal presence among his disciples accompanies the Church’s pilgrimage of faith.",
        citation: "Redemptoris Mater, §§24–27",
        url: "https://www.vatican.va/content/john-paul-ii/en/encyclicals/documents/hf_jp-ii_enc_25031987_redemptoris-mater.html",
        urlLabel: "Vatican.va",
      },
      {
        id: "rosarium",
        title: "Rosarium Virginis Mariae",
        author: "St. John Paul II",
        year: "16 October 2002",
        kind: "Apostolic Letter",
        why: "Added the five Luminous Mysteries and gave the Rosary a fresh theology as a contemplative prayer with Mary of the face of Christ.",
        excerpt: "John Paul II presents the Rosary as contemplation of Christ with Mary. Its Marian character serves a Christ-centred prayer that remembers the mysteries of his saving life.",
        citation: "Rosarium Virginis Mariae, §§1, 3, 10–17",
        url: "https://www.vatican.va/content/john-paul-ii/en/apost_letters/2002/documents/hf_jp-ii_apl_20021016_rosarium-virginis-mariae.html",
        urlLabel: "Vatican.va",
      },
    ],
  },
  {
    id: "discernment",
    label: "Discernment of apparitions",
    kicker: "1978 · 2024",
    intro: "The two Vatican documents that govern how the Church discerns and pronounces on alleged Marian apparitions — the foundation of our editorial standards.",
    entries: [
      {
        id: "cdf-1978",
        title: "Normae Congregationis (1978 Norms)",
        author: "Congregation for the Doctrine of the Faith",
        year: "25 February 1978",
        kind: "Norms",
        why: "For 46 years the framework by which local Ordinaries evaluated alleged apparitions — the three-tier verdict (constat de supernaturalitate / non constat / constat de non supernaturalitate).",
        excerpt: "The 1978 norms direct the competent authority to assess the facts, the personal qualities of alleged visionaries, doctrinal soundness and spiritual fruits, alongside possible negative evidence. They were superseded in 2024.",
        citation: "Normae Congregationis (1978), I–II · published in 2012",
        url: "https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_con_cfaith_doc_20120515_norme-apparizioni_en.html",
        urlLabel: "Vatican.va (2012 publication)",
      },
      {
        id: "ddf-2024",
        title: "Norms for Alleged Supernatural Phenomena",
        author: "Dicastery for the Doctrine of the Faith · Prefect Fernández, approved by Pope Francis",
        year: "17 May 2024",
        kind: "Norms (superseding the 1978 Normae)",
        why: "The new discernment framework — six possible verdicts instead of three (Nihil obstat · Prae oculis habeatur · Curatur · Sub mandato · Prohibetur et obstruatur · Declaratio de non supernaturalitate). Final decisions are reserved to the Dicastery.",
        excerpt: "Ordinarily, discernment now concerns the pastoral value and risks of a phenomenon, without declaring a supernatural origin. A nihil obstat permits prudent adherence, not an obligation of faith; the Pope may authorize an exceptional procedure.",
        citation: "DDF Norms (2024) · Presentation and I, §§11–17",
        url: "https://www.vatican.va/roman_curia/congregations/cfaith/documents/rc_ddf_doc_20240517_norme-fenomeni-soprannaturali_en.html",
        urlLabel: "Vatican.va",
      },
    ],
  },
];

export const LIBRARY_KINDS = {
  Prayer: "bg-gold/15 text-gold",
  "Ecumenical Council": "bg-marian/12 text-marian",
  "Apostolic Constitution": "bg-marian/12 text-marian",
  "Dogmatic Constitution": "bg-marian/12 text-marian",
  "Apostolic Exhortation": "bg-slateblue/12 text-slateblue",
  Encyclical: "bg-slateblue/12 text-slateblue",
  "Apostolic Letter": "bg-slateblue/12 text-slateblue",
  Norms: "bg-charcoal/10 text-charcoal",
  "Norms (superseding the 1978 Normae)": "bg-charcoal/10 text-charcoal",
};
